import React, { useCallback, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type { PreviewVoucherTextReplacement } from '~/services/PromoService';

type Props = {
  textReplacements: Array<PreviewVoucherTextReplacement>;
  defaultExpanded?: boolean;
  tableAriaLabel: string;
};

export default function VoucherTextReplacements({ textReplacements, defaultExpanded = false, tableAriaLabel }: Props) {
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const visibleTextReplacements = textReplacements.filter((replacement) => replacement.name.trim());
  const handleCopyTextReplacementToken = useCallback(async (replacementName: string) => {
    const token = `<${replacementName.trim()}>`;
    await navigator.clipboard.writeText(token);
    setCopiedToken(token);
    window.setTimeout(() => setCopiedToken((current) => (current === token ? null : current)), 2000);
  }, []);

  if (visibleTextReplacements.length === 0) {
    return null;
  }

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters variant="outlined" sx={{ maxWidth: 760 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box>
          <Typography variant="subtitle2">Text replacements</Typography>
          <Typography variant="body2" color="text.secondary">
            Replaced in the title, voucher amount display override, description, and details.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={tableAriaLabel}>
            <TableHead>
              <TableRow>
                <TableCell>Replacement key</TableCell>
                <TableCell>Preview value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleTextReplacements.map((replacement) => {
                const replacementToken = `<${replacement.name.trim()}>`;
                const isCopied = copiedToken === replacementToken;
                return (
                  <TableRow key={replacement.name}>
                    <TableCell component="th" scope="row" sx={{ width: '36%', overflowWrap: 'anywhere' }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
                        >
                          {replacementToken}
                        </Typography>
                        <IconButton
                          size="small"
                          type="button"
                          onClick={() => handleCopyTextReplacementToken(replacement.name)}
                          aria-label={`Copy ${replacementToken}`}
                          title={isCopied ? 'Copied' : `Copy ${replacementToken}`}
                        >
                          {isCopied ? (
                            <CheckIcon fontSize="small" color="success" />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {replacement.value.trim() || ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}
